import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as XLSX from 'xlsx';
import DataSourceService from '../../services/DataSourceService';
import SemanticModelProvisioningService from '../../services/SemanticModelProvisioningService';
import {
  DEFAULT_INGESTION_BATCH_SIZE,
  INGESTION_BATCH_HELP,
  INGESTION_BATCH_OPTIONS
} from '../settings/ingestionBatchOptions';

const MAX_CSV_FILES = 10;
// Mismo tope que MAX_SHEETS_PER_FILE en whisperbi-back-security/functions/
// file-ingestion/app.py -- si se mandaran más, la validación del backend
// (sheet_table_name_overrides, tope 50 entradas) rechazaría la conexión
// entera con un 400 antes de subir nada.
const MAX_EXCEL_SHEETS = 50;
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const EXCEL_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const CSV_CONTENT_TYPE = 'text/csv';

// Espacios -> guion bajo, como pidió el usuario. El nombre final de tabla
// Glue lo termina de sanear el backend (normalize_id: minúsculas, solo
// [a-z0-9_]) sin importar lo que se escriba acá -- esto es solo el default
// mostrado en el paso 2, editable.
const sanitizeDefaultName = (raw) => (raw || '').trim().replace(/\s+/g, '_');

const stripExtension = (fileName) => fileName.replace(/\.[^/.]+$/, '');

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Ejecuta `worker` sobre `items` en bloques de `batchSize`: arranca el
 * bloque siguiente solo cuando terminó (bien o mal) todo el anterior, para
 * no saturar los servicios con todos los archivos a la vez.
 */
export const runInBatches = async (items, batchSize, worker) => {
  for (let start = 0; start < items.length; start += batchSize) {
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(items.slice(start, start + batchSize).map((item) => worker(item)));
  }
};

const uploadFileWithProgress = (uploadUrl, file, contentType, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Error subiendo el archivo (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Error de red subiendo el archivo'));
    xhr.send(file);
  });

/**
 * Trae la lista de nombres de hoja de un Excel SIN parsear los datos
 * (bookSheets:true -- solo lee la tabla de contenidos del workbook), para
 * poder mostrarlos en el paso 2 del wizard ANTES de subir nada.
 */
const readExcelSheetNames = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'array', bookSheets: true });
        resolve(workbook.SheetNames || []);
      } catch (error) {
        reject(new Error('No se pudo leer el archivo Excel. Verificá que no esté dañado.'));
      }
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsArrayBuffer(file);
  });

const STATUS_META = {
  queued: { label: 'En cola', color: 'default' },
  uploading: { label: 'Subiendo…', color: 'info' },
  processing: { label: 'Procesando…', color: 'info' },
  success: { label: 'Lista', color: 'success' },
  schema_review: { label: 'Requiere revisión', color: 'warning' },
  error: { label: 'Error', color: 'error' }
};

/**
 * Wizard de carga de Excel/CSV con schema Iceberg destino explícito (a
 * diferencia del flujo de "conexión de Excel" en SemanticConnections.jsx,
 * que siempre cae en el schema "Default" auto-provisionado del tenant).
 * Reusa exactamente el mismo pipeline de subida (DataSourceService ->
 * presigned S3 PUT -> confirm-upload -> file-ingestion async) y de
 * provisión del modelo semántico (SemanticModelProvisioningService) -- ver
 * ese flujo en SemanticConnections.jsx.
 */
const FileUploadWizard = ({ open, onClose, accountId, icebergSchemaId, glueDatabaseName, onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [mode, setMode] = useState('excel'); // 'excel' | 'csv'
  const [excelFile, setExcelFile] = useState(null);
  const [csvFiles, setCsvFiles] = useState([]);
  const [plannedTables, setPlannedTables] = useState([]); // [{id, kind, file, sheetIndex, sheetName, tableName}]
  const [isParsingExcel, setIsParsingExcel] = useState(false);
  const [selectError, setSelectError] = useState(null);
  const [selectWarning, setSelectWarning] = useState(null);
  const [rows, setRows] = useState([]); // estado del paso 3, uno por tabla planificada
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasFinished, setHasFinished] = useState(false);
  const [batchSize, setBatchSize] = useState(DEFAULT_INGESTION_BATCH_SIZE);

  const resetAll = () => {
    setActiveStep(0);
    setMode('excel');
    setExcelFile(null);
    setCsvFiles([]);
    setPlannedTables([]);
    setSelectError(null);
    setSelectWarning(null);
    setRows([]);
    setIsSubmitting(false);
    setHasFinished(false);
    setBatchSize(DEFAULT_INGESTION_BATCH_SIZE);
  };

  const handleClose = () => {
    if (isSubmitting) return; // no cerrar a mitad de una carga en curso
    resetAll();
    onClose();
  };

  const handleModeChange = (event, newMode) => {
    if (!newMode) return;
    setMode(newMode);
    setExcelFile(null);
    setCsvFiles([]);
    setPlannedTables([]);
    setSelectError(null);
    setSelectWarning(null);
  };

  const handleExcelFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setSelectError(null);
    setSelectWarning(null);
    setIsParsingExcel(true);
    try {
      let sheetNames = await readExcelSheetNames(file);
      if (sheetNames.length === 0) {
        setSelectError('El archivo no tiene hojas con datos.');
        return;
      }
      if (sheetNames.length > MAX_EXCEL_SHEETS) {
        setSelectWarning(
          `El archivo tiene ${sheetNames.length} hojas -- solo se van a procesar las primeras ${MAX_EXCEL_SHEETS} (límite del sistema).`
        );
        sheetNames = sheetNames.slice(0, MAX_EXCEL_SHEETS);
      }
      setExcelFile(file);
      setPlannedTables(
        sheetNames.map((sheetName, sheetIndex) => ({
          id: `sheet-${sheetIndex}`,
          kind: 'excel_sheet',
          file,
          sheetIndex,
          sheetName,
          tableName: sanitizeDefaultName(sheetName) || `hoja_${sheetIndex}`
        }))
      );
    } catch (error) {
      setSelectError(error.message);
    } finally {
      setIsParsingExcel(false);
    }
  };

  const handleCsvFilesChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    if (files.length > MAX_CSV_FILES) {
      setSelectError(`Podés subir hasta ${MAX_CSV_FILES} archivos CSV por vez (elegiste ${files.length}).`);
      return;
    }
    setSelectError(null);
    setSelectWarning(null);
    setCsvFiles(files);
    setPlannedTables(
      files.map((file, index) => ({
        id: `csv-${index}-${file.name}`,
        kind: 'csv',
        file,
        tableName: sanitizeDefaultName(stripExtension(file.name)) || `tabla_${index}`
      }))
    );
  };

  const handleTableNameChange = (id, value) => {
    setPlannedTables((prev) => prev.map((table) => (table.id === id ? { ...table, tableName: value } : table)));
  };

  const nameValidation = (() => {
    const errors = {};
    const seen = new Map();
    plannedTables.forEach((table) => {
      const trimmed = (table.tableName || '').trim();
      if (!trimmed) {
        errors[table.id] = 'El nombre no puede estar vacío.';
        return;
      }
      const key = trimmed.toLowerCase();
      if (seen.has(key)) {
        errors[table.id] = 'Nombre repetido -- cada tabla necesita un nombre distinto.';
        errors[seen.get(key)] = 'Nombre repetido -- cada tabla necesita un nombre distinto.';
      } else {
        seen.set(key, table.id);
      }
    });
    return errors;
  })();

  const canGoToStep2 = plannedTables.length > 0 && !selectError && !isParsingExcel;
  const canStartUpload = plannedTables.length > 0 && Object.keys(nameValidation).length === 0;

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const pollConnectionUntilDone = async (connectionId, onTick) => {
    const startedAt = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const connection = await DataSourceService.getRawSourceConnection(connectionId);
      onTick(connection);
      if (['active', 'ready', 'error'].includes(connection.connection_status)) {
        return connection;
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        throw new Error('El procesamiento está tardando más de lo esperado. Podés revisar el estado más tarde en el Explorador.');
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_INTERVAL_MS);
    }
  };

  const provisionSemanticModelBestEffort = async (connection) => {
    try {
      await SemanticModelProvisioningService.provisionFromConnection(connection);
    } catch (error) {
      // Best-effort a propósito -- mismo criterio que SemanticConnections.jsx:
      // la tabla Iceberg física ya se escribió bien, que falle la
      // provisión del modelo semántico no debe mostrarse como un error de
      // la carga en sí (queda cubierto por reconcileFileConnections en la
      // próxima carga de la página).
      console.error('No se pudo provisionar el modelo semántico:', error);
    }
  };

  const sheetStatusToRowStatus = (sheetStatus) => {
    if (sheetStatus === 'success') return 'success';
    if (sheetStatus === 'error') return 'error';
    if (sheetStatus === 'schema_review_pending') return 'schema_review';
    return 'processing'; // "iceberg_job_started" u otro estado intermedio
  };

  const runExcelUpload = async () => {
    const sheetTableNameOverrides = {};
    plannedTables.forEach((table) => {
      sheetTableNameOverrides[String(table.sheetIndex)] = table.tableName.trim();
    });

    let connection;
    try {
      connection = await DataSourceService.createFileConnection({
        name: excelFile.name,
        connectionType: 'excel',
        icebergSchemaId,
        sheetTableNameOverrides,
        // Las hojas las encadena el backend respetando este tamaño de bloque.
        ingestionBatchSize: batchSize
      });
    } catch (error) {
      setRows((prev) => prev.map((row) => ({ ...row, status: 'error', errorMessage: error.message })));
      return;
    }

    const connectionId = connection.source_connection_id;
    setRows((prev) => prev.map((row) => ({ ...row, status: 'uploading', connectionId })));

    try {
      const uploadUrlData = await DataSourceService.getFileUploadUrl(connectionId, excelFile.name, EXCEL_CONTENT_TYPE);
      await uploadFileWithProgress(uploadUrlData.upload_url, excelFile, EXCEL_CONTENT_TYPE, (pct) => {
        setRows((prev) => prev.map((row) => ({ ...row, progress: pct })));
      });
      await DataSourceService.confirmFileUpload(connectionId, uploadUrlData.s3_key);
    } catch (error) {
      setRows((prev) => prev.map((row) => ({ ...row, status: 'error', errorMessage: error.message })));
      return;
    }

    setRows((prev) => prev.map((row) => ({ ...row, status: 'processing' })));

    let finalConnection;
    try {
      finalConnection = await pollConnectionUntilDone(connectionId, (connection_) => {
        const sheetProgress = Array.isArray(connection_.sheet_progress) ? connection_.sheet_progress : [];
        setRows((prev) =>
          prev.map((row) => {
            const sheetResult = sheetProgress.find((s) => s.sheet_index === row.sheetIndex);
            if (!sheetResult) return row;
            return {
              ...row,
              status: sheetStatusToRowStatus(sheetResult.status),
              errorMessage: sheetResult.error_message_user || null,
              finalTableName: sheetResult.athena_table || row.finalTableName
            };
          })
        );
      });
    } catch (error) {
      setRows((prev) => prev.map((row) => (row.status === 'processing' ? { ...row, status: 'error', errorMessage: error.message } : row)));
      return;
    }

    if (finalConnection.connection_status === 'active' || finalConnection.connection_status === 'ready') {
      await provisionSemanticModelBestEffort(finalConnection);
    }
  };

  const runSingleCsvRow = async (table) => {
    let connection;
    try {
      connection = await DataSourceService.createFileConnection({
        name: table.file.name,
        connectionType: 'csv',
        icebergSchemaId,
        tableNameOverride: table.tableName.trim()
      });
    } catch (error) {
      updateRow(table.id, { status: 'error', errorMessage: error.message });
      return;
    }

    const connectionId = connection.source_connection_id;
    updateRow(table.id, { status: 'uploading', connectionId });

    try {
      const uploadUrlData = await DataSourceService.getFileUploadUrl(connectionId, table.file.name, CSV_CONTENT_TYPE);
      await uploadFileWithProgress(uploadUrlData.upload_url, table.file, CSV_CONTENT_TYPE, (pct) => {
        updateRow(table.id, { progress: pct });
      });
      await DataSourceService.confirmFileUpload(connectionId, uploadUrlData.s3_key);
    } catch (error) {
      updateRow(table.id, { status: 'error', errorMessage: error.message });
      return;
    }

    updateRow(table.id, { status: 'processing' });

    let finalConnection;
    try {
      finalConnection = await pollConnectionUntilDone(connectionId, (connection_) => {
        const sheetResult = Array.isArray(connection_.sheet_progress) ? connection_.sheet_progress[0] : null;
        if (sheetResult) {
          updateRow(table.id, {
            status: sheetStatusToRowStatus(sheetResult.status),
            errorMessage: sheetResult.error_message_user || null,
            finalTableName: sheetResult.athena_table
          });
        }
      });
    } catch (error) {
      updateRow(table.id, { status: 'error', errorMessage: error.message });
      return;
    }

    if (finalConnection.connection_status === 'error' && finalConnection.error_message_user) {
      updateRow(table.id, { status: 'error', errorMessage: finalConnection.error_message_user });
    }
    if (finalConnection.connection_status === 'active' || finalConnection.connection_status === 'ready') {
      await provisionSemanticModelBestEffort(finalConnection);
    }
  };

  const handleStartUpload = async () => {
    setActiveStep(2);
    setIsSubmitting(true);
    setRows(plannedTables.map((table) => ({ ...table, status: 'queued', progress: 0, errorMessage: null })));
    try {
      if (mode === 'excel') {
        await runExcelUpload();
      } else {
        // Cada CSV es su propia conexión: los bloques se arman acá.
        await runInBatches(plannedTables, batchSize, runSingleCsvRow);
      }
    } finally {
      setIsSubmitting(false);
      setHasFinished(true);
      onComplete?.();
    }
  };

  const renderStepOne = () => (
    <Box>
      <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} sx={{ mb: 2 }}>
        <ToggleButton value="excel">Excel (una tabla por hoja)</ToggleButton>
        <ToggleButton value="csv">CSV (hasta {MAX_CSV_FILES} archivos)</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'excel' ? (
        <Box>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={isParsingExcel}>
            {isParsingExcel ? 'Leyendo hojas…' : excelFile ? excelFile.name : 'Elegir archivo Excel'}
            <input type="file" hidden accept=".xlsx,.xls" onChange={handleExcelFileChange} />
          </Button>
          {isParsingExcel && <CircularProgress size={18} sx={{ ml: 2 }} />}
        </Box>
      ) : (
        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
          {csvFiles.length > 0 ? `${csvFiles.length} archivo(s) CSV seleccionados` : 'Elegir archivos CSV'}
          <input type="file" hidden accept=".csv" multiple onChange={handleCsvFilesChange} />
        </Button>
      )}

      {selectError && <Alert severity="error" sx={{ mt: 2 }}>{selectError}</Alert>}
      {selectWarning && <Alert severity="warning" sx={{ mt: 2 }}>{selectWarning}</Alert>}

      {plannedTables.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Se van a crear {plannedTables.length} tabla(s) en el schema{' '}
          <strong>{glueDatabaseName || icebergSchemaId}</strong>.
        </Typography>
      )}
    </Box>
  );

  const renderStepTwo = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Revisá y, si querés, cambiá el nombre de cada tabla antes de subirla.
      </Typography>
      {plannedTables.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            fullWidth
            label={mode === 'excel' ? 'Procesar las hojas' : 'Procesar los archivos'}
            value={batchSize}
            onChange={(event) => setBatchSize(Number(event.target.value))}
            helperText={INGESTION_BATCH_HELP}
          >
            {INGESTION_BATCH_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {mode === 'excel' ? option.sheetsLabel : option.filesLabel}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
      <List dense>
        {plannedTables.map((table) => (
          <ListItem key={table.id} sx={{ alignItems: 'flex-start' }}>
            <ListItemText
              sx={{ flex: '0 0 45%', mr: 2 }}
              primary={table.kind === 'excel_sheet' ? table.sheetName : table.file.name}
              secondary={table.kind === 'excel_sheet' ? `Hoja de ${table.file.name}` : 'Archivo CSV'}
            />
            <TextField
              size="small"
              fullWidth
              value={table.tableName}
              onChange={(event) => handleTableNameChange(table.id, event.target.value)}
              error={Boolean(nameValidation[table.id])}
              helperText={nameValidation[table.id] || ' '}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderStepThree = () => (
    <Box>
      <List dense>
        {rows.map((row) => {
          const meta = STATUS_META[row.status] || STATUS_META.queued;
          return (
            <ListItem key={row.id} sx={{ alignItems: 'flex-start' }}>
              <ListItemText
                sx={{ flex: '0 0 45%', mr: 2 }}
                primary={row.tableName}
                secondary={row.errorMessage || (row.kind === 'excel_sheet' ? `Hoja: ${row.sheetName}` : row.file.name)}
              />
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                {row.status === 'uploading' && (
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress variant="determinate" value={row.progress || 0} />
                  </Box>
                )}
                {row.status === 'processing' && <LinearProgress sx={{ flex: 1 }} />}
                {row.status === 'success' && <CheckCircleIcon color="success" fontSize="small" />}
                {row.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                {row.status === 'schema_review' && <WarningAmberIcon color="warning" fontSize="small" />}
                <Chip size="small" label={meta.label} color={meta.color} />
              </Box>
            </ListItem>
          );
        })}
      </List>

      {hasFinished && (
        <Alert severity={rows.some((r) => r.status === 'error') ? 'warning' : 'success'} sx={{ mt: 2 }}>
          {rows.filter((r) => r.status === 'success').length} de {rows.length} tabla(s) creadas correctamente.
          {rows.some((r) => r.status === 'schema_review') &&
            ' Algunas quedaron pendientes de revisión de esquema -- podés resolverlas desde el ícono de aviso en la tabla.'}
        </Alert>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Subir Excel / CSV</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>Archivos</StepLabel>
          </Step>
          <Step>
            <StepLabel>Nombres de tabla</StepLabel>
          </Step>
          <Step>
            <StepLabel>Carga</StepLabel>
          </Step>
        </Stepper>
        {activeStep === 0 && renderStepOne()}
        {activeStep === 1 && renderStepTwo()}
        {activeStep === 2 && renderStepThree()}
      </DialogContent>
      <DialogActions>
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button variant="contained" disabled={!canGoToStep2} onClick={() => setActiveStep(1)}>
              Siguiente
            </Button>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>Atrás</Button>
            <Button variant="contained" disabled={!canStartUpload} onClick={handleStartUpload}>
              Iniciar carga
            </Button>
          </>
        )}
        {activeStep === 2 && (
          <Button variant="contained" disabled={isSubmitting} onClick={handleClose}>
            {isSubmitting ? 'Subiendo…' : 'Cerrar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadWizard;
